import React, { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";
import { StoreContext } from "../context/StoreContext";
import { TAVAHOT_PRIVACY_URL, TAVAHOT_TERMS_URL } from "../constants/policies";

const Locate = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { setSource, setRequestId, userDetails, userId, accessToken, transientUser } =
    useContext(StoreContext);

  const sessionId = searchParams.get("sessionId");
  const locationParam = searchParams.get("location");

  const [locationValue, setLocationValue] = useState("");
  const [initLoading, setInitLoading] = useState(true);
  const [initError, setInitError] = useState(null);
  const [tncLoading, setTncLoading] = useState(false);
  const [tncError, setTncError] = useState(null);

  const formattedLocation = useMemo(() => {
    if (!locationValue) return "";
    const [lat, lng] = locationValue.split(",");
    if (!lat || !lng) return locationValue;
    const latNum = Number(lat);
    const lngNum = Number(lng);
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return locationValue;
    return `${latNum.toFixed(5)}, ${lngNum.toFixed(5)}`;
  }, [locationValue]);

  useEffect(() => {
    const initialize = async () => {
      setInitError(null);

      const rawLocation = locationParam?.trim();
      if (!rawLocation) {
        setInitError("Location is missing in the link.");
        setInitLoading(false);
        return;
      }

      const [latRaw, lngRaw] = rawLocation.split(",").map((coord) => coord.trim());
      if (
        !latRaw ||
        !lngRaw ||
        Number.isNaN(parseFloat(latRaw)) ||
        Number.isNaN(parseFloat(lngRaw))
      ) {
        setInitError("Location is invalid.");
        setInitLoading(false);
        return;
      }

      const lat = parseFloat(latRaw);
      const lng = parseFloat(lngRaw);
      const normalizedLocation = `${lat},${lng}`;
      setLocationValue(normalizedLocation);
      setSource(normalizedLocation);

      if (!sessionId) {
        setInitError("Session id is missing in the link.");
        setInitLoading(false);
        return;
      }

      setRequestId(sessionId);

      try {
        await userDetails(sessionId);
      } catch (e) {
        console.error("Implicit auth failed on locate:", e);
        setInitError("We couldn't authenticate you. Please try again.");
      } finally {
        setInitLoading(false);
      }
    };

    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationParam, sessionId, setSource, setRequestId]);

  const handleFindRestaurants = async () => {
    setTncError(null);

    if (!locationValue) {
      setTncError("Location is missing. Please refresh the link.");
      return;
    }

    if (!sessionId) {
      setTncError("Session id is missing. Please refresh the link.");
      return;
    }

    // For transient users, accept T&C before proceeding.
    if (transientUser === true) {
      if (!userId || !accessToken) {
        setTncError("Missing authentication details. Please try again.");
        return;
      }

      setTncLoading(true);

      try {
        const response = await axios.post(
          "/api/userservice/tnc/accept",
          { userId },
          {
            headers: {
              accept: "application/json",
              "Content-Type": "application/json",
              "auth-token": accessToken,
              "user-id": userId,
            },
          }
        );

        if (!response?.data?.success) {
          setTncError(
            response?.data?.message ||
              "Please try again! We’re continuously improving the system."
          );
          return;
        }
      } catch (e) {
        console.error("T&C accept failed on locate:", e);
        setTncError(
          "Please try again! We couldn’t accept the terms right now."
        );
        return;
      } finally {
        setTncLoading(false);
      }
    }

    const query = new URLSearchParams({
      location: locationValue,
      sessionId,
    }).toString();

    navigate(`/suggest-restaurant?${query}`);
  };

  if (initLoading) {
    return (
      <div className="flex justify-center items-center h-screen pt-24 px-4">
        <div className="text-center">
          <TailSpin
            height="80"
            width="80"
            color="#FBC02D"
            ariaLabel="loading"
            radius="1"
            visible={true}
          />
          <p className="text-gray-600 mt-4">Preparing your experience...</p>
        </div>
      </div>
    );
  }

  if (initError) {
    return (
      <div className="flex justify-center items-center h-screen pt-24 px-4">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            We hit a snag
          </h2>
          <p className="text-gray-600 mb-6">{initError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-[#FBC02D] text-white px-6 py-3 rounded-lg hover:bg-[#F9A825] transition-colors font-semibold"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-screen flex flex-col justify-center items-center pt-24 px-4">
      <div className="max-w-md w-full text-center space-y-8">
        <div className="space-y-3">
          <h1 className="text-3xl font-bold text-[#171725]">
            Find restaurants near you
          </h1>
          <p className="text-gray-600">
            We’ll show options you can preorder around your current location.
          </p>
        </div>

        <div className="bg-gray-100 rounded-2xl p-5 shadow-inner">
          <p className="text-sm text-gray-500 uppercase tracking-wide mb-1">
            Current location
          </p>
          <p className="text-lg font-semibold text-gray-800">
            {formattedLocation}
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={handleFindRestaurants}
            disabled={tncLoading}
            className={`bg-[#FBC02D] text-white px-8 py-4 w-full rounded-lg transition-colors font-semibold ${
              tncLoading ? "opacity-70 cursor-not-allowed" : "hover:bg-[#F9A825]"
            }`}
          >
            {tncLoading ? "Please wait..." : "Restaurants Around Me"}
          </button>
          <p className="text-sm text-gray-500">
            By tapping, I accept the{" "}
            <a
              href={TAVAHOT_TERMS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-gray-700"
            >
              terms of service
            </a>{" "}
            &{" "}
            <a
              href={TAVAHOT_PRIVACY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline text-gray-700"
            >
              privacy policy
            </a>
            .
          </p>
          {tncError && (
            <p className="text-sm text-red-600 mt-1">{tncError}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Locate;

